/**
 * Drop shadow for image placeholders.
 *
 * Built as an image filter (and not as a fabric shadow on the object) for the same reason the
 *  outline is: the effect has to live INSIDE the image bitmap. Two consequences we depend on:
 *   - the bitmap keeps its exact pixel dimensions, so the fabric object's geometry never changes
 *     and the PNG the production file processor exports keeps the placeholder size it has today;
 *   - we never touch fabric's `shadow` property, so `fabric.Group.shouldCache()` keeps caching the
 *     placeholder. A group whose child returns willDrawShadow() === true stops caching, and then
 *     its clipPath is applied with 'destination-in' straight onto the main canvas context, erasing
 *     everything already painted.
 *
 * Like the outline, the picture is scaled down to make room for the effect, so the shadow is
 *  always visible no matter how much the customer zooms the image inside the placeholder — but
 *  only by as much as it actually needs: the room is measured against the opaque silhouette, not
 *  against the bitmap rectangle, so an image with transparent padding (a cut-out, a logo, a
 *  remove-background photo) lets the shadow spread into that padding and is never resized.
 */
const { DEFAULT_SHADOW_FILL, SHADOW_TYPE } = require('../utils/shadowDefaults.js');

fabric.Image.filters.ShadowFilter = fabric.util.createClass(fabric.Image.filters.BaseFilter, {
    type: 'ShadowFilter',

    // Fractions of the image diagonal reached at 100% on each slider. Same idea as the outline's
    //  `outlineReduction`: everything is relative to the bitmap so a value looks identical in the
    //  ~700px preview canvas and in a multi-thousand pixel production file.
    OFFSET_RATIO_AT_100: 0.05,
    BLUR_RATIO_AT_100: 0.12,

    // canvas2d turns a shadowBlur of b into a gaussian of sigma = b/2, so the blur only dies out
    //  around 3 sigma = 1.5b. Reserving less cuts the tail at the bitmap edge and leaves a straight
    //  line: at 1.15b (2.3 sigma) the cut still measured 1.3% opacity on a production file.
    BLUR_SPREAD_SAFETY: 1.5,

    // Pixels at or below this alpha don't cast a visible shadow, so they don't count as silhouette
    ALPHA_THRESHOLD: 8,

    // Never shrink past this. A scale of 0 makes drawImage() paint nothing and the picture would
    //  vanish instead of just losing room to the shadow. Unreachable with the ratios above (a
    //  square bitmap bottoms out around 0.6), it is here so raising them can't blank an image.
    MIN_FIT_SCALE: 0.05,

    initialize: function (options) {
        options = options || {};

        this.shadowType = typeof options.shadowType === 'undefined' ? null : options.shadowType;
        this.shadowOffset = options.shadowOffset || 0;
        this.shadowAngle = options.shadowAngle || 0;
        this.shadowBlur = options.shadowBlur || 0;
        // Same default the text shadow uses (see BoundedText.setShadowColor)
        this.shadowColor = options.shadowColor || DEFAULT_SHADOW_FILL;
    },

    /**
     * Distance/blur in bitmap pixels for a given image diagonal.
     * Kept public so the placeholder (and the tests) can ask how much room the effect needs
     *  without reaching into applyTo2d().
     */
    calculateShadowMetrics: function (diagonal) {
        const distance = diagonal * this.OFFSET_RATIO_AT_100 * ((this.shadowOffset || 0) / 100);
        const angleRad = ((this.shadowAngle || 0) * Math.PI) / 180;

        // Only the blurred variant uses the blur slider; the plain drop shadow keeps hard edges.
        const isBlurred = this.shadowType === SHADOW_TYPE.BLURRED;
        const blur = isBlurred ? diagonal * this.BLUR_RATIO_AT_100 * ((this.shadowBlur || 0) / 100) : 0;

        return {
            offsetX: distance * Math.cos(angleRad),
            offsetY: distance * Math.sin(angleRad),
            blur: blur
        };
    },

    /**
     * Bounding box of the opaque pixels, or null when the image is fully transparent.
     *
     * Only the first and the last opaque pixel of each row can move the box, so each row is walked
     *  inwards from both ends and abandoned as soon as both are found. That makes the result exact
     *  and, on the usual bitmap (a photo that fills its frame), costs two reads per row.
     *
     * It used to read one pixel every Nth column and row instead, which was not sound: a silhouette
     *  thinner than the stride leaves no trace in the samples, and padding the box afterwards can't
     *  bring back what was never read. A hatched image came out with no shadow at all because every
     *  sample landed on a transparent column.
     */
    getSilhouetteBounds: function (imageData) {
        const data = imageData.data;
        const width = imageData.width;
        const height = imageData.height;
        const alphaThreshold = this.ALPHA_THRESHOLD;

        let minX = width;
        let minY = height;
        let maxX = -1;
        let maxY = -1;

        for (let y = 0; y < height; y++) {
            const rowStart = y * width * 4;

            let firstX = -1;
            for (let x = 0; x < width; x++) {
                if (data[rowStart + x * 4 + 3] > alphaThreshold) {
                    firstX = x;
                    break;
                }
            }

            // Fully transparent row, it can't grow the box in any direction
            if (firstX < 0) {
                continue;
            }

            let lastX = firstX;
            for (let x = width - 1; x > firstX; x--) {
                if (data[rowStart + x * 4 + 3] > alphaThreshold) {
                    lastX = x;
                    break;
                }
            }

            if (firstX < minX) minX = firstX;
            if (lastX > maxX) maxX = lastX;
            if (y < minY) minY = y;
            maxY = y;
        }

        if (maxX < 0) {
            return null;
        }

        return {
            left: minX,
            top: minY,
            right: maxX + 1,
            bottom: maxY + 1
        };
    },

    /**
     * How much the picture has to shrink (1 = not at all) for the silhouette AND its shadow to
     *  fit inside the bitmap. Everything is scaled around the bitmap center, so each edge of the
     *  union rectangle gives its own limit and the tightest one wins.
     */
    calculateFitScale: function (bounds, shadow, width, height) {
        const spread = shadow.blur * this.BLUR_SPREAD_SAFETY;

        // The shadow is the silhouette displaced by the offset and grown by the blur
        const union = {
            left: Math.min(bounds.left, bounds.left + shadow.offsetX - spread),
            top: Math.min(bounds.top, bounds.top + shadow.offsetY - spread),
            right: Math.max(bounds.right, bounds.right + shadow.offsetX + spread),
            bottom: Math.max(bounds.bottom, bounds.bottom + shadow.offsetY + spread)
        };

        const centerX = width / 2;
        const centerY = height / 2;
        let scale = 1;

        if (union.left < centerX) {
            scale = Math.min(scale, centerX / (centerX - union.left));
        }
        if (union.right > centerX) {
            scale = Math.min(scale, (width - centerX) / (union.right - centerX));
        }
        if (union.top < centerY) {
            scale = Math.min(scale, centerY / (centerY - union.top));
        }
        if (union.bottom > centerY) {
            scale = Math.min(scale, (height - centerY) / (union.bottom - centerY));
        }

        return Math.max(this.MIN_FIT_SCALE, Math.min(1, scale));
    },

    /**
     * @param {Object} options
     * @param {ImageData} options.imageData Pixel data of the image being filtered
     * @param {HTMLImageElement} options.originalEl Source image element
     */
    applyTo2d: function (options) {
        if (this.shadowType === null || typeof this.shadowType === 'undefined') {
            return;
        }

        const imageData = options.imageData;
        const width = imageData.width;
        const height = imageData.height;

        // If the image is not loaded yet we can't apply the filter
        if (!options.originalEl || width <= 1 || height <= 1) {
            return;
        }

        const diagonal = Math.sqrt(width * width + height * height);
        const shadow = this.calculateShadowMetrics(diagonal);

        // Nothing to cast: no displacement and no blur means the shadow sits exactly under the
        //  image and would never be seen
        if (!shadow.offsetX && !shadow.offsetY && !shadow.blur) {
            return;
        }

        const bounds = this.getSilhouetteBounds(imageData);
        if (!bounds) {
            return;
        }

        // Shrink only by what the shadow actually needs. A single uniform factor (and not one per
        //  axis like the outline does) so non-square images keep their aspect ratio.
        const scale = this.calculateFitScale(bounds, shadow, width, height);

        // Whole pixels: a destination rectangle off the pixel grid gets its edge antialiased by
        //  the resampler, and the shadow underneath then bleeds through that edge as a line along
        //  the whole side - top and bottom at a 0/360 degree angle, left and right at 90.
        const drawWidth = Math.max(1, Math.round(width * scale));
        const drawHeight = Math.max(1, Math.round(height * scale));
        const drawLeft = Math.round((width - drawWidth) / 2);
        const drawTop = Math.round((height - drawHeight) / 2);

        // Copy the incoming pixels (they already went through the previous filters in the
        //  pipeline, e.g. the outline) into a canvas we can draw from.
        const sourceCanvas = document.createElement('canvas');
        sourceCanvas.width = width;
        sourceCanvas.height = height;
        sourceCanvas.getContext('2d').putImageData(imageData, 0, 0);

        // Resized in a canvas of its own so the drawImage that casts the shadow stays a 1:1 blit
        //  onto integer coordinates: it resamples nothing, so it leaves no soft edge to bleed.
        const scaledCanvas = document.createElement('canvas');
        scaledCanvas.width = drawWidth;
        scaledCanvas.height = drawHeight;
        scaledCanvas.getContext('2d').drawImage(sourceCanvas, 0, 0, drawWidth, drawHeight);

        const targetCanvas = document.createElement('canvas');
        targetCanvas.width = width;
        targetCanvas.height = height;
        const ctx = targetCanvas.getContext('2d');

        // canvas2d paints the shadow and the image in a single drawImage call, shadow underneath.
        // Offsets and blur are scaled along with the picture so the effect stays proportional.
        ctx.shadowColor = this.shadowColor;
        ctx.shadowBlur = shadow.blur * scale;
        ctx.shadowOffsetX = shadow.offsetX * scale;
        ctx.shadowOffsetY = shadow.offsetY * scale;
        ctx.drawImage(scaledCanvas, drawLeft, drawTop);

        imageData.data.set(ctx.getImageData(0, 0, width, height).data);
    }
});

fabric.Image.filters.ShadowFilter.fromObject = function (object) {
    return new fabric.Image.filters.ShadowFilter(object);
};
